/**
 * Notes Entity Store
 *
 * Dedicated `entityRegistry` scope for `Note` / `Content.Note`.
 *
 * Unlike `globalEntityStore`, this store does not fetch anything on its own —
 * it's a passive cache + `entityRegistry` scope adapter, populated by:
 *   1. `useAppGalleryEntities` seeding it after its own REST fetch (see
 *      gui/src/hooks/useAppGalleryEntities.ts), and
 *   2. Live upserts/deletes dispatched through `entityRegistry` from anywhere
 *      else in the app (entityActions after create/update/delete, and the
 *      org-wide live-update bus in orgYDocStore.ts for changes made in other
 *      tabs/windows).
 *
 * Notes previously fell through to the default 'space' scope
 * (spaceEntityStore), which is bounded to the 8 most-recently-opened spaces
 * per tab — the root cause of the Notes overview showing a different list
 * per browser window. Giving Notes their own always-present scope means an
 * upsert for a note in a space that isn't currently cached still lands
 * somewhere durable, instead of being silently dropped.
 */

import { create } from 'zustand';
import { registerScopeAdapter, notifyChange as notifyRegistryChange } from './entityRegistry';
import type { BaseEntityType } from '../types/entities';

interface NotesEntityState {
  byId: Map<string, BaseEntityType>;
  idsByType: Map<string, Set<string>>;
  bumpVersion: number;
}

interface NotesEntityActions {
  getById: (id: string) => BaseEntityType | undefined;
  getByType: (entityType: string) => BaseEntityType[];
  upsert: (entity: BaseEntityType) => void;
  deleteEntity: (id: string) => void;
  getAll: () => BaseEntityType[];
}

export type NotesEntityStore = NotesEntityState & NotesEntityActions;

export const useNotesEntityStore = create<NotesEntityStore>((set, get) => ({
  byId: new Map(),
  idsByType: new Map(),
  bumpVersion: 0,

  getById: (id: string) => get().byId.get(id),

  getByType: (entityType: string) => {
    const ids = get().idsByType.get(entityType);
    if (!ids || ids.size === 0) return [];
    const byId = get().byId;
    const out: BaseEntityType[] = [];
    for (const id of ids) {
      const e = byId.get(id);
      if (e) out.push(e);
    }
    return out;
  },

  getAll: () => {
    const seen = new Set<string>();
    const out: BaseEntityType[] = [];
    for (const e of get().byId.values()) {
      const id = e._id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
    return out;
  },

  upsert: (entity: BaseEntityType) => {
    if (!entity || !entity._id) return;
    set((s) => {
      const byId = new Map(s.byId);
      const idsByType = new Map(s.idsByType);

      const prev = byId.get(entity._id);
      const prevType = prev?.entityType;
      const typeBucketChanged = !prevType || prevType !== entity.entityType;

      byId.set(entity._id, entity);
      if ((entity as any).id && (entity as any).id !== entity._id) {
        byId.set((entity as any).id, entity);
      }

      if (prevType && prevType !== entity.entityType) {
        const oldIds = idsByType.get(prevType);
        if (oldIds) {
          const next = new Set(oldIds);
          next.delete(entity._id);
          if (next.size === 0) idsByType.delete(prevType);
          else idsByType.set(prevType, next);
        }
      }

      if (typeBucketChanged) {
        const existingIds = idsByType.get(entity.entityType);
        if (!existingIds) {
          idsByType.set(entity.entityType, new Set([entity._id]));
        } else if (!existingIds.has(entity._id)) {
          const ids = new Set(existingIds);
          ids.add(entity._id);
          idsByType.set(entity.entityType, ids);
        }
      }

      return { byId, idsByType, bumpVersion: s.bumpVersion + 1 };
    });
  },

  deleteEntity: (id: string) => {
    if (!id) return;
    const prev = get().byId.get(id);
    if (!prev) return;
    set((s) => {
      const byId = new Map(s.byId);
      const idsByType = new Map(s.idsByType);
      byId.delete(id);
      if ((prev as any).id && (prev as any).id !== id) byId.delete((prev as any).id);
      const ids = idsByType.get(prev.entityType);
      if (ids) {
        const next = new Set(ids);
        next.delete(id);
        if (next.size === 0) idsByType.delete(prev.entityType);
        else idsByType.set(prev.entityType, next);
      }
      return { byId, idsByType, bumpVersion: s.bumpVersion + 1 };
    });
  },
}));

registerScopeAdapter('notes', {
  getById: (id) => useNotesEntityStore.getState().getById(id),
  getByType: (entityType) => useNotesEntityStore.getState().getByType(entityType),
  upsert: (entity) => useNotesEntityStore.getState().upsert(entity),
  deleteEntity: (id) => useNotesEntityStore.getState().deleteEntity(id),
  dumpAll: () => useNotesEntityStore.getState().getAll(),
});

// Bridge bumpVersion ticks → entityRegistry.subscribeToChanges, same pattern
// as globalEntityStore, so consumers of the registry's change bus (including
// useAppGalleryEntities) see live upserts/deletes routed here.
useNotesEntityStore.subscribe((state, prev) => {
  if (state.bumpVersion === prev.bumpVersion) return;
  notifyRegistryChange();
});
